import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';
import { media, SelectableCard } from '@payright/web-components';
import { AuLicenceDetails } from './licence-details/au-licence-details';
import { ControlledInputField, ControlledDatePicker } from '../form-fields';
import type {
  IdentificationDetailsData as BnplCheckoutApiIdentificationDetailsData,
  IdentificationType as BnplCheckoutApiIdentificationType,
} from '@payright/types-bnpl-checkout-api';
import getConstants from './../../util/constants';

const { IDENTIFICATION_MAX_YEARS_EXPIRY_FROM_TODAY } = getConstants();

export type IdentificationDetailsData = BnplCheckoutApiIdentificationDetailsData;
export type IdentificationType = BnplCheckoutApiIdentificationType;

export interface IdentificationDetailsProps {
  existingCustomer: boolean;
  editing: boolean;
  handleIdentificationType: Function;
  identificationType: IdentificationType;
}

export interface IdentificationDetailsProps {
  existingCustomer: boolean;
  editing: boolean;
}

const SCIdentificationDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  .identification-types {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .identification-details-fields {
    display: grid;
    grid-template-columns: auto;
    column-gap: 1rem;
    row-gap: 2rem;
  }

  ${media.min.medium} {
    .identification-types {
      flex-direction: row;
    }

    .identification-details-fields {
      grid-template-columns: repeat(3, minmax(10px, 1fr));
    }

    .md\\:col-span-2 {
      grid-column: span 2;
    }

    .md\\:col-span-3 {
      grid-column: span 3;
    }
  }
`;

export const IdentificationDetails = ({
  existingCustomer,
  editing,
  handleIdentificationType,
  identificationType,
}: IdentificationDetailsProps) => {
  const { errors, setValue, register } = useFormContext<{
    identificationType: IdentificationType;
    identificationDetails: IdentificationDetailsData;
  }>();

  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (Object.keys(errors).length > 0 && errors.identificationDetails && sectionRef.current) {
      // check for null and focus target on block with errors
      sectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [errors]); // Errors object changes on submit

  useEffect(() => {
    register({ name: 'identificationDetails.identificationType' });
    setValue('identificationDetails.identificationType', identificationType);
  }, [identificationType, register, setValue]);

  return (
    <SCIdentificationDetails>
      <div ref={sectionRef} style={{ position: 'relative', top: '-2em', display: 'none' }} />
      <div className="identification-types">
        <SelectableCard
          selected={identificationType === 'license'}
          handleSelect={handleIdentificationType}
          title="Licence"
          name={'license' as IdentificationType}
          body=""
        />
        <SelectableCard
          selected={identificationType === 'passport'}
          handleSelect={handleIdentificationType}
          title="Passport"
          name={'passport' as IdentificationType}
          body=""
        />
        <SelectableCard
          selected={identificationType === 'other'}
          handleSelect={handleIdentificationType}
          title="Other"
          name={'other' as IdentificationType}
          body=""
        />
      </div>

      {identificationType === 'license' && (
        <AuLicenceDetails existingCustomer={existingCustomer} editing={editing} />
      )}

      {identificationType === 'passport' && (
        <div className="identification-details-fields">
          <ControlledInputField
            className="md:col-span-3"
            name="identificationDetails.passportNumber"
            rules={{
              required: 'Passport number is required',
              pattern: {
                value: /^(?!^0+$)[a-zA-Z0-9]{4,20}$/,
                message: 'Invalid passport number',
              },
            }}
            error={(errors.identificationDetails as any)?.passportNumber?.message as string}
            readOnly={existingCustomer && !editing}
            isPrivate
          >
            Number
          </ControlledInputField>

          <ControlledDatePicker
            className="md:col-span-2"
            name="identificationDetails.passportExpiry"
            rules={{
              required: 'Passport Expiry is required',
              validate: (value: any) => {
                const expiry = new Date(value);
                const today = new Date();
                today.setDate(today.getDate() + 1);
                if (expiry.getTime() < today.getTime()) {
                  return 'Enter a valid expiry';
                }
              },
            }}
            error={(errors.identificationDetails as any)?.passportExpiry?.message as string}
            readOnly={existingCustomer && !editing}
            startYear={new Date().getFullYear()}
            endYear={new Date().getFullYear() + IDENTIFICATION_MAX_YEARS_EXPIRY_FROM_TODAY}
            isPrivate
          >
            Expiry
          </ControlledDatePicker>
        </div>
      )}

      {identificationType === 'other' && (
        <ControlledInputField
          readOnly={existingCustomer && !editing}
          rules={{
            required: 'Other ID is required',
            minLength: 2,
          }}
          error={(errors.identificationDetails as any)?.otherIdType.message as string}
          name="identificationDetails.otherIdType"
        >
          Other type of government photo ID
        </ControlledInputField>
      )}
    </SCIdentificationDetails>
  );
};
